import React, { useMemo, useContext, useCallback, useRef } from "react";
import "../patterns.css";
import Item from "./Item";
import ButtonCollection from "./ButtonCollection";
import Background from "./Background";
import ContainerEffectOverlay from "./ContainerEffectOverlay";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleProvider } from "../../editor/context/DragHandleContext";
import { makeSortableId } from "../../editor/utils/reorderCache";
import AddButtonWithIndicator from "../../editor/components/AddButtonWithIndicator";
import AddButtonOverlay from "../../editor/add-buttons/AddButtonOverlay";
import { useAddElement } from "../../editor/context/AddElementContext";
import { EditContext } from "../../editor/context/EditContext";
import { Z_INDEX } from "../../core/utils/zIndexScale";
import useHeroBase from "./hooks/useHeroBase";

const SortableItemWrapper = React.memo(({ id, dataAttrs, children }) => {
  const stableData = useMemo(() => dataAttrs, [JSON.stringify(dataAttrs ?? null)]);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id, 
    data: stableData 
  });
  
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragHandleProvider value={{ dragListeners: listeners, dragAnywhereEnabled: true }}>
        {children}
      </DragHandleProvider>
    </div>
  );
});

SortableItemWrapper.displayName = "SortableItemWrapper";

const hasFullData = (child) => {
  if (!child) return false;
  const minimalFields = ['id', 'type', 'order'];
  const allFields = Object.keys(child);
  return allFields.some(field => !minimalFields.includes(field));
};

const HeroManual = ({ item, containerWidth: providedContainerWidth }) => {
  const heroBase = useHeroBase(item, providedContainerWidth);
  const { data, heroRootRef, containerClasses, containerStyle, fullBleedStyle, contentInsetStyle, responsiveHeight, baseHeightPx, bp, width, forceSingleColumn } = heroBase;

  const layout = data?.hero_layout || data?.layout;

  const orderedChildren = useMemo(() => {
    const arr = Array.isArray(data.children) ? data.children.filter(Boolean) : [];
    return arr.slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  }, [data.children]);

  // Resolve per-breakpoint hero layout (columns-first): JSON list string like "[6]"
  const pickColumnsList = (bpKey) => {
    const raw = layout?.[bpKey];
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n) && n > 0);
    }
    if (typeof raw === "string" && raw.trim()) {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) return arr.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n) && n > 0);
      } catch {}
    }
    return [6];
  };
  const columnsList = pickColumnsList(bp);

  const columns = useMemo(() => {
    if (forceSingleColumn) {
      return [orderedChildren];
    }
    const result = [];
    const counts = Array.isArray(columnsList) && columnsList.length ? columnsList : [6];
    const items = orderedChildren.slice();

    let idx = 0;
    for (let ci = 0; ci < counts.length; ci++) {
      const cnt = Math.max(0, parseInt(counts[ci], 10) || 0);
      const colItems = items.slice(idx, idx + cnt);
      result.push(colItems);
      idx += cnt;
    }
    if (idx < items.length && result.length > 0) {
      result[result.length - 1] = result[result.length - 1].concat(items.slice(idx));
    }

    if (result.length === 0) result.push(items);
    return result;
  }, [orderedChildren, columnsList, forceSingleColumn]);

  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;
  const { openAddModal } = useAddElement();

  // Hero-level drop target. Without this, the only way to drag an element into
  // a Hero was to land precisely on an existing Hero child sortable — which is
  // impossible for an empty Hero and very fiddly for a sparse one. Exposing the
  // whole Hero interior as a ``parent:hero:<id>`` droppable (mirroring the
  // ``parent:component`` / ``parent:page`` targets) lets users drop a Title (or
  // any item) anywhere inside the Hero. The drag-end handler in
  // ``EditWebsitePage`` recognises this id and calls ``moveItem`` with
  // ``toParentHeroId``. ``data`` carries the owning component so the handler can
  // invalidate the right queries. Safe to call unconditionally: outside a
  // DndContext (public render) the hook returns an inert no-op ref.
  const heroDroppableId = `parent:hero:${data.id}`;
  const { setNodeRef: setHeroDropRef, isOver: isHeroDropOver } = useDroppable({
    id: heroDroppableId,
    data: {
      parentType: "hero",
      parentId: data.id,
      parentComponentId: data.component,
    },
  });

  const handleAddClick = useCallback((e) => {
    e.stopPropagation();
    openAddModal({ 
      componentId: data.component, 
      heroId: data.id, 
      order: orderedChildren.length 
    }, e);
  }, [openAddModal, data.component, data.id, orderedChildren.length]);

  const renderItem = useCallback((child, columnWidthPx) => {
    const isFull = hasFullData(child);
    
    return child.type === "button_collection" ? (
      <ButtonCollection 
        {...(isFull ? { item: child } : { itemRef: { id: child.id, type: child.type } })}
        containerWidth={columnWidthPx}
      />
    ) : (
      <Item 
        {...(isFull ? { item: child } : { itemRef: { id: child.id, type: child.type } })}
        containerWidth={columnWidthPx}
      />
    );
  }, []);

  const colCount = Math.max(1, columns.length || 1);

  const computedWidths = useMemo(() => {
    const n = colCount;
    if (!n || n <= 0) return [100];

    const equalSplit = (count) => {
      const base = 100 / Math.max(1, count);
      return Array.from({ length: Math.max(1, count) }, () => base);
    };

    const normalizeToHundred = (arr) => {
      const filtered = (Array.isArray(arr) ? arr : []).map((x) => (Number.isFinite(x) ? Math.max(0, x) : 0));
      if (filtered.length === 0) return equalSplit(n);
      const sum = filtered.reduce((a, b) => a + b, 0);
      if (sum <= 0) return equalSplit(n);
      const scaled = filtered.map((x) => (x / sum) * 100);
      const drift = 100 - scaled.reduce((a, b) => a + b, 0);
      scaled[scaled.length - 1] = Math.max(0, scaled[scaled.length - 1] + drift);
      return scaled;
    };

    const eff = data?.effective_widths && Array.isArray(data.effective_widths[bp])
      ? data.effective_widths[bp]
      : null;
    if (Array.isArray(eff) && eff.length > 0) {
      let arr = eff.slice(0, n);
      if (arr.length < n) {
        const missing = n - arr.length;
        const reservedPer = 100 / n;
        const reserved = reservedPer * missing;
        const sumProvided = arr.reduce((a, b) => a + (Number.isFinite(b) ? Math.max(0, b) : 0), 0);
        if (sumProvided > 0) {
          const scale = (100 - reserved) / sumProvided;
          arr = arr.map((x) => Math.max(0, (Number.isFinite(x) ? x : 0) * scale));
        } else {
          arr = [];
        }
        arr = arr.concat(Array.from({ length: missing }, () => reservedPer));
        const drift = 100 - arr.reduce((a, b) => a + b, 0);
        arr[n - 1] = Math.max(0, arr[n - 1] + drift);
      } else {
        arr = normalizeToHundred(arr);
      }
      return arr;
    }

    const hcw = data?.hero_column_widths && Array.isArray(data.hero_column_widths[bp])
      ? data.hero_column_widths[bp]
      : null;
    let arr = Array.isArray(hcw) ? hcw.slice(0, n) : [];
    if (arr.length === 0) {
      arr = equalSplit(n);
    } else if (arr.length < n) {
      const missing = n - arr.length;
      const reservedPer = 100 / n;
      const reserved = reservedPer * missing;
      const sumProvided = arr.reduce((a, b) => a + (Number.isFinite(b) ? Math.max(0, b) : 0), 0);
      if (sumProvided > 0) {
        const scale = (100 - reserved) / sumProvided;
        arr = arr.map((x) => Math.max(0, (Number.isFinite(x) ? x : 0) * scale));
      } else {
        arr = [];
      }
      arr = arr.concat(Array.from({ length: missing }, () => reservedPer));
      const drift = 100 - arr.reduce((a, b) => a + b, 0);
      arr[n - 1] = Math.max(0, arr[n - 1] + drift);
    } else if (arr.length > n) {
      arr = normalizeToHundred(arr.slice(0, n));
    } else {
      arr = normalizeToHundred(arr);
    }

    return arr;
  }, [colCount, data?.effective_widths, data?.hero_column_widths, bp]);

  const gridStyle = useMemo(() => ({
    display: "grid",
    gridTemplateColumns: Array.isArray(computedWidths) && computedWidths.length === colCount
      ? computedWidths.map((w) => `${w}%`).join(" ")
      : `repeat(${colCount}, minmax(0, 1fr))`,
    gap: "12px",
    alignItems: "stretch"
  }), [colCount, computedWidths]);

  const renderColumn = useCallback((columnItems, colIdx) => {
    const columnWidthPercent = computedWidths[colIdx] || (100 / colCount);
    const columnWidthPx = Math.floor(width * (columnWidthPercent / 100));
    
    const columnSortableIds = columnItems.map((child) => 
      makeSortableId({ type: child.type, id: child.id })
    );

    const columnContent = (
      <div
        className="flex flex-col gap-3 justify-center"
        key={`hero-col-${colIdx}`}
        style={{ minHeight: "100%" }}
      >
        {columnItems.map((child, itemIdx) => {
          const globalIndex = orderedChildren.findIndex(
            (e) => e.id === child.id && e.type === child.type
          );
          const inner = (
            <div className="w-full">
              {renderItem(child, columnWidthPx)}
            </div>
          );

          if (!isEditMode) {
            return <div key={`hero-item-${child.type}-${child.id}`}>{inner}</div>;
          }

          return (
            <div key={`hero-item-wrapper-${child.type}-${child.id}`} className="relative group w-full" style={{ zIndex: Z_INDEX.CONTENT }}>
              <SortableItemWrapper
                key={`hero-item-${child.type}-${child.id}`}
                id={makeSortableId({ type: child.type, id: child.id })}
                dataAttrs={{
                  parentType: "hero",
                  parentId: data.id,
                  parentComponentId: data.component,
                  kind: "item",
                  itemType: child.type,
                  index: globalIndex
                }}
              >
                {inner}
              </SortableItemWrapper>
              
              {itemIdx === 0 && child.type !== "button_collection" && (
                <AddButtonOverlay
                  layer="hero-item-before"
                  overlayId={`hero-item-before-${data.id}-${child.id}`}
                  className="absolute top-0 left-0 w-full -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator
                    onClick={(e) => { 
                      e.stopPropagation();
                      openAddModal({ 
                        componentId: data.component, 
                        heroId: data.id, 
                        order: globalIndex 
                      }, e); 
                    }}
                  />
                </AddButtonOverlay>
              )}
              
              {child.type !== "button_collection" && (
                <AddButtonOverlay
                  layer="hero-item-after"
                  overlayId={`hero-item-after-${data.id}-${child.id}`}
                  shift={globalIndex === orderedChildren.length - 1 ? "left" : "none"}
                  className="absolute bottom-0 left-0 w-full translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                >
                  <AddButtonWithIndicator
                    onClick={(e) => { 
                      e.stopPropagation();
                      openAddModal({ 
                        componentId: data.component, 
                        heroId: data.id, 
                        order: globalIndex + 1 
                    }, e); 
                  }}
                />
              </AddButtonOverlay>
              )}
            </div>
          );
        })}
      </div>
    );

    if (isEditMode && columnItems.length > 0) {
      return (
        <SortableContext
          key={`sortable-col-${colIdx}`}
          items={columnSortableIds}
          strategy={verticalListSortingStrategy}
        >
          {columnContent}
        </SortableContext>
      );
    }

    return columnContent;
  }, [orderedChildren, isEditMode, data.id, data.component, renderItem, computedWidths, colCount, width, openAddModal]);

  const contentGrid = (
    <div className="w-full" style={gridStyle} data-testid="hero-content-grid">
      {columns.map((columnItems, colIdx) => renderColumn(columnItems, colIdx))}
    </div>
  );

  const contentWithDnD = isEditMode ? (
    <div className="relative w-full group">
      {contentGrid}
      <AddButtonOverlay
        layer="hero-grid-after"
        overlayId={`hero-grid-after-${data.id}`}
        shift="left"
        className="relative h-0 w-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
      >
        <AddButtonWithIndicator 
          onClick={handleAddClick}
          className="absolute bottom-0 left-0 w-full translate-y-1/2"
        />
      </AddButtonOverlay>
    </div>
  ) : (
    contentGrid
  );

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
        hoverClass={heroBase.insetShadowClass}
      />
      <ContainerEffectOverlay data={data} />
      <div
        ref={isEditMode ? setHeroDropRef : undefined}
        className={
          responsiveHeight
            ? "relative z-10 overflow-visible flex items-center w-full"
            : "absolute inset-0 z-10 overflow-visible flex items-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Drop-target highlight: a subtle ring while an element is being
            dragged over the Hero interior, so users get clear feedback that
            releasing here nests the element inside the Hero. */}
        {isEditMode && isHeroDropOver && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-1 z-20 rounded-md ring-2 ring-blue-400/70 ring-inset"
          />
        )}
        <div className="w-full" style={contentInsetStyle || undefined}>
          {contentWithDnD}
        </div>
      </div>
    </div>
  );
};

export default HeroManual;
